import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type RowValueFilterLogicalOp = 'and' | 'or';

export type RowValueFilterOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte';

export interface RowValueFilterRule {
  // Action à appliquer si la condition est vraie: garder ou supprimer la ligne
  action: 'keep' | 'remove';
  // Colonne sur laquelle appliquer la condition (clé interne du tableau importé)
  columnKey: string;
  // Opérateur de comparaison (égal, différent, supérieur, inférieur, etc.)
  operator: RowValueFilterOperator;
  // Valeur de comparaison (texte ou nombre sous forme de chaîne)
  value: string;
  // Opérateur logique par rapport à la règle précédente (ET / OU)
  logicalOperatorWithPrevious?: RowValueFilterLogicalOp;
}

export interface StoreRegulationTemplate {
  rowsToRemove: number[];
  columnsToRemove: string[];
  // Index/clé de la ligne utilisée comme ligne de titres (si l'utilisateur a promu une ligne en en-tête)
  headerRowIndex?: number;
  columnMapping?: Record<string, string>;
  // Noms de colonnes pour lesquelles une ligne doit être supprimée si la valeur est vide/null
  rowFilterEmptyColumns?: string[];
  // Si true, les colonnes entièrement vides sont automatiquement masquées
  hideEmptyColumns?: boolean;
  // Ancien filtre simple (gardé pour compatibilité et migration douce)
  rowValueFilterColumn?: string;
  rowValueFilterValue?: string;
  // Nouveau: liste de règles avancées pour filtrer les lignes
  rowValueFilterRules?: RowValueFilterRule[];
}

export interface StoreRegulationState {
  templates: Record<string, StoreRegulationTemplate>;
}

const initialState: StoreRegulationState = {
  templates: {},
};

export const storeRegulationSlice = createSlice({
  name: 'storeRegulation',
  initialState,
  reducers: {
    setTemplateForLocation: (
      state,
      action: PayloadAction<{
        locationId: string;
        rowsToRemove: number[];
        columnsToRemove: string[];
        headerRowIndex?: number;
        columnMapping?: Record<string, string>;
        rowFilterEmptyColumns?: string[];
        hideEmptyColumns?: boolean;
        rowValueFilterColumn?: string;
        rowValueFilterValue?: string;
        rowValueFilterRules?: RowValueFilterRule[];
      }>
    ) => {
      const {
        locationId,
        rowsToRemove,
        columnsToRemove,
        headerRowIndex,
        columnMapping,
        rowFilterEmptyColumns,
        hideEmptyColumns,
        rowValueFilterColumn,
        rowValueFilterValue,
        rowValueFilterRules,
      } = action.payload;

      const existing = state.templates[locationId] || {};

      state.templates[locationId] = {
        ...existing,
        rowsToRemove,
        columnsToRemove,
        ...(headerRowIndex !== undefined ? { headerRowIndex } : {}),
        ...(columnMapping !== undefined ? { columnMapping } : {}),
        ...(rowFilterEmptyColumns !== undefined
          ? { rowFilterEmptyColumns }
          : {}),
        ...(hideEmptyColumns !== undefined ? { hideEmptyColumns } : {}),
        ...(rowValueFilterColumn !== undefined
          ? { rowValueFilterColumn }
          : {}),
        ...(rowValueFilterValue !== undefined
          ? { rowValueFilterValue }
          : {}),
        ...(rowValueFilterRules !== undefined
          ? { rowValueFilterRules }
          : {}),
      };
    },
  },
});

export const { setTemplateForLocation } = storeRegulationSlice.actions;

export default storeRegulationSlice.reducer;
